import { useDispatch, useSelector } from 'react-redux';
import { ChevronRightIcon } from '@rsos/assets/icons';
import { Text } from '@rsos/base-components';
import { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import {
  openResourcePanel,
  setResourcePanelView,
  setSelectedRequest,
  useGetTransportsQueryWithPolling,
  type EtlTransportResponse,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import {
  BannerWrapper,
  IconWrapper,
  IconWrapperChevron,
} from '../../../AirAmbulanceForm/Banner/StatusBanner.styles';
import {
  formatTransportStatuses,
  getStatusPageState,
} from '../../../AirAmbulanceForm/etl/etlStatusUtils';

/**
 * Displays etl_integration's based status banners for air ambulance transports
 * associated with the currently selected call.
 */
function ResourceRequestStatusBanner(): JSX.Element | null {
  const dispatch = useDispatch();
  const selectedCallerID = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls.selectedCallerID,
  );

  const { data } = useGetTransportsQueryWithPolling();
  const transports = data?.transports;
  if (!transports) {
    // could be that the query is still loading. We can detect this with isLoading, but we don't care in this case we just don't want to render the banner
    return null;
  }

  const matchingTransports = transports.filter(
    t => t.caller_id === selectedCallerID,
  );
  if (matchingTransports.length === 0) {
    return null;
  }

  // TODO: When adding support for non-air-ambulance types of requests, make
  //   this generic enough to show banners for the different types of resource
  //   requests. Each type may have a different nested compnent here or the type
  //   can provide a "statusMessage" property that we can just render in the
  //   banner without needing a nested component.
  return (
    <>
      {matchingTransports.map(transport => (
        <TransportBanner
          key={transport.gmr_transport_id}
          transport={transport}
          dispatch={dispatch}
        />
      ))}
    </>
  );
}

function TransportBanner({
  transport,
  dispatch,
}: {
  transport: EtlTransportResponse;
  dispatch: ReturnType<typeof useDispatch>;
}) {
  const formattedStatuses = formatTransportStatuses(transport.statuses);
  const statusPageState = getStatusPageState(transport, formattedStatuses);
  const latestStatus = formattedStatuses[0];

  const handleClick = () => {
    dispatch(setSelectedRequest(transport.gmr_transport_id));
    dispatch(openResourcePanel());
    dispatch(setResourcePanelView('air_ambulance'));
  };

  const IconComponent = statusPageState.icon;
  // TODO: When we support mutliple different types of resource requests in addition to GMR/Air Ambulance (e.g. Tow Pro), we can
  //   make the label for the type of the resource request generic and come from
  //   the actual request
  const RESOURCE_REQUEST_TYPE_LABEL = 'Air ambulance';
  const displayText = latestStatus?.display
    ? `${RESOURCE_REQUEST_TYPE_LABEL} status: ${latestStatus?.display}`
    : statusPageState.message;

  return (
    <BannerWrapper bgColor={statusPageState.bgColor} horizontal>
      <IconWrapper>
        <IconComponent color="background" height={16} width={16} />
      </IconWrapper>
      <Text color="background" ml="12px" size="normal">
        {displayText}
      </Text>
      <IconWrapperChevron onClick={handleClick}>
        <ChevronRightIcon
          height={10}
          width={10}
          viewboxWidth={16}
          viewboxHeight={16}
          data-name="resource-request-banner-chevron"
        />
      </IconWrapperChevron>
    </BannerWrapper>
  );
}

export default ResourceRequestStatusBanner;
